// Ethanol–water properties from the International Alcoholometric Tables
// (OIML R22, 1975) — the same basis AlcoDens and HMRC's tables use.
//
// rho(p, t): density in kg/m³ of an ethanol–water mixture with ethanol mass
// fraction p (0–1) at t °C (valid −20 to +40 °C). Strength by volume is at
// 20 °C: ABV = 100 · p · rho(p, 20) / rho(1, 20).
// Checked: pure ethanol 789.24 and water 998.20 kg/m³ at 20 °C; water at
// 0/10/30/40 °C; 40 % vol = 33.30 % mass, 948.05 kg/m³.

const A = [998.20123, -192.9769495, 389.1238958, -1668.103923, 13522.15441, -88292.78388, 306287.4042, -613838.1234, 747017.2998, -547846.1354, 223446.0334, -39032.85426];
const B = [-2.0618513e-1, -5.2682542e-3, 3.6130013e-5, -3.8957702e-7, 7.1693540e-9, -9.9739231e-11];
const C = [
  [1.693443461530087e-1, -1.046914743455169e1, 7.196353469546523e1, -7.047478054272792e2, 3.924090430035045e3, -1.210164659068747e4, 2.248646550400788e4, -2.605562982188164e4, 1.852373922069467e4, -7.420201433430137e3, 1.285617841998974e3],
  [-1.193013005057010e-2, 2.517399633803461e-1, -2.170575700536993, 1.353034988843029e1, -5.029988758547014e1, 1.096355666577570e2, -1.422753946421155e2, 1.080435942856230e2, -4.414153236817392e1, 7.442971530188783],
  [-6.802995733503803e-4, 1.876837790289664e-2, -2.002561813734156e-1, 1.022992966719220, -2.895696483903638, 4.810060584300675, -4.672147440794683, 2.458043105903461, -5.411227621436812e-1],
  [4.075376675622027e-6, -8.763058573471110e-6, 6.515031360099368e-6, -1.515784836987210e-6],
  [-2.788074354782409e-8, 1.345612883493354e-8],
];

export function rho(p, t = 20) {
  let r = A[0];
  for (let k = 2; k <= 12; k += 1) r += A[k - 1] * p ** (k - 1);
  const d = t - 20;
  for (let k = 1; k <= 6; k += 1) r += B[k - 1] * d ** k;
  for (let i = 1; i <= 5; i += 1) for (let k = 1; k <= C[i - 1].length; k += 1) r += C[i - 1][k - 1] * p ** k * d ** i;
  return r;
}

export const RHO_ETHANOL_20 = rho(1, 20); // 789.24
export const RHO_WATER_20 = rho(0, 20); // 998.20

/** % vol at 20 °C for an ethanol mass fraction. */
export const abvFromP = (p) => (100 * p * rho(p, 20)) / RHO_ETHANOL_20;

function solve(f, target, lo = 0, hi = 1) {
  // f is monotonic on [lo, hi]; bisection to full precision.
  const up = f(hi) > f(lo);
  for (let i = 0; i < 80; i += 1) {
    const m = (lo + hi) / 2;
    if (up ? f(m) < target : f(m) > target) lo = m; else hi = m;
  }
  return (lo + hi) / 2;
}

/** Ethanol mass fraction for a % vol at 20 °C. */
export const pFromAbv = (abv) => (abv <= 0 ? 0 : abv >= 100 ? 1 : solve(abvFromP, abv));

/** Ethanol mass fraction from a density (kg/m³) at t °C. Density falls as p rises. */
export const pFromRho = (r, t = 20) => solve((p) => rho(p, t), r);

// Glass alcoholometers are calibrated at 20 °C; the glass expands with
// temperature (OIML: cubic expansion 25 × 10⁻⁶ /°C).
const GLASS = 25e-6;

/** True % vol at 20 °C from an alcoholometer (hydrometer) reading taken at t °C. */
export function trueAbvFromReading(reading, t) {
  const markDensity = rho(pFromAbv(reading), 20);
  const liquid = markDensity / (1 + GLASS * (t - 20));
  return abvFromP(pFromRho(liquid, t));
}

/** What an alcoholometer would read at t °C in a spirit of true strength abv. */
export function readingFromTrueAbv(abv, t) {
  const liquid = rho(pFromAbv(abv), t);
  const mark = liquid * (1 + GLASS * (t - 20));
  return abvFromP(pFromRho(mark, 20));
}

/** Everything about a spirit of a given true strength, at a temperature. */
export function properties(abv, t = 20) {
  const p = pFromAbv(abv);
  return {
    abv, abw: p * 100, p,
    rho20: rho(p, 20), rhoT: rho(p, t),
    sg: rho(p, 20) / RHO_WATER_20, // 20/20
    ukProof: abv * 1.75, usProof: abv * 2,
    lpaPerLitre: abv / 100,
  };
}

/**
 * Sweetened spirit: density from true strength and sugar (g/L), with
 * dissolved sugar taking k litres per kg (sucrose ≈ 0.625). The
 * hydroalcoholic part is what's left of each litre.
 */
export function liqueurDensity(abv, sugarGL, k = 0.625) {
  const S = sugarGL / 1000; // kg per litre
  const solventL = 1 - k * S;
  if (solventL <= 0) return NaN;
  const solventAbv = abv / solventL;
  if (solventAbv > 100) return NaN;
  return solventL * rho(pFromAbv(solventAbv), 20) + sugarGL; // kg/m³ = g/L
}

/**
 * The hydrometer reading (apparent % vol at 20 °C) of a sweetened spirit —
 * its obscured strength. null when the liquid is denser than water: an
 * alcoholometer reads below zero.
 */
export function apparentAbv(abv, sugarGL, k = 0.625) {
  const r = liqueurDensity(abv, sugarGL, k);
  if (!Number.isFinite(r) || r >= RHO_WATER_20) return null;
  return abvFromP(pFromRho(r, 20));
}

/** True % vol from the liquid's density at 20 °C (kg/m³) and its sugar content. */
export function trueAbvFromDensity(density, sugarGL, k = 0.625) {
  const maxAbv = 100 * (1 - (k * sugarGL) / 1000);
  if (!(maxAbv > 0)) return NaN;
  const lo = liqueurDensity(maxAbv, sugarGL, k); const hi = liqueurDensity(0, sugarGL, k);
  if (density < lo - 1e-9 || density > hi + 1e-9) return NaN; // outside what this sugar level allows
  // Density falls as true ABV rises (for fixed sugar).
  return solve((a) => liqueurDensity(a, sugarGL, k), density, 0, maxAbv);
}

/** True % vol from an apparent (hydrometer, 20 °C) reading and the sugar content. */
export function trueAbvFromApparent(apparent, sugarGL, k = 0.625) {
  return trueAbvFromDensity(rho(pFromAbv(apparent), 20), sugarGL, k);
}

/**
 * True strength from a strength check: a hydrometer reading or a density at
 * t °C, or an ABV already corrected ('direct'). With sugar, the temperature
 * correction is made as for ethanol–water and the sugar's obscuration
 * removed at 20 °C — close for liqueurs near 20 °C.
 */
export function strengthFromReading({ method, reading, temp = 20, density, sugar = 0, k = 0.625 }) {
  const t = Number(temp); const S = Number(sugar) || 0;
  if (method === 'direct') {
    const abv = Number(reading);
    return { abv, density20: S > 0 ? liqueurDensity(abv, S, k) : rho(pFromAbv(abv), 20) };
  }
  if (method === 'hydrometer') {
    const apparent20 = trueAbvFromReading(Number(reading), t);
    const abv = S > 0 ? trueAbvFromApparent(apparent20, S, k) : apparent20;
    return { abv, apparent20, density20: S > 0 ? liqueurDensity(abv, S, k) : rho(pFromAbv(abv), 20) };
  }
  if (method === 'density') {
    const d = Number(density);
    const pApp = pFromRho(Math.min(d, rho(0, t)), t);
    const density20 = d * (rho(pApp, 20) / rho(pApp, t));
    const abv = S > 0 ? trueAbvFromDensity(density20, S, k) : abvFromP(pFromRho(density20, 20));
    return { abv, density20 };
  }
  return { abv: NaN };
}

/**
 * Bring V litres at abvNow to target by adding water (addAbv 0) or a spirit
 * (addAbv, e.g. 96.4). Ethanol–water: by mass, so contraction is allowed
 * for. With sugar in the batch, by pure alcohol with volumes added.
 */
export function correctTo({ volume, abvNow, target, addAbv = 0, sugar = 0 }) {
  const V = Number(volume); const a = Number(abvNow); const tg = Number(target); const s = Number(addAbv);
  if (!(V > 0) || !(tg > 0) || a === tg) return null;
  if ((tg - a) * (s - tg) <= 0) return null; // can't get there with this addition
  if (Number(sugar) > 0) {
    const add = (V * (tg - a)) / (s - tg);
    return { add, final: V + add, by: 'volume' };
  }
  const pa = pFromAbv(a); const ps = pFromAbv(s); const pt = pFromAbv(tg);
  const Ma = (V * rho(pa, 20)) / 1000;
  const Ms = (Ma * (pa - pt)) / (pt - ps);
  return { add: Ms / (rho(ps, 20) / 1000), addKg: Ms, final: (Ma + Ms) / (rho(pt, 20) / 1000), by: 'mass' };
}

/**
 * Sugar (g/L) from a measured density and a known true strength (Joe,
 * 22 Oct 2026). Density at t °C is first brought to 20 °C as for an
 * ethanol–water mix of that strength. Density rises with sugar, so the
 * sugar that gives this density is found by bisection. NaN when the
 * figures don't fit (e.g. lighter than the spirit with no sugar at all).
 */
export function sugarFromDensity(density, abv, temp = 20, k = 0.625) {
  const a = Number(abv); const t = Number(temp);
  if (!(a >= 0 && a < 100)) return NaN;
  const p = pFromAbv(a);
  const d20 = Number(density) * (rho(p, 20) / rho(p, t));
  const maxS = ((1 - a / 100) * 1000) / k - 1e-6; // sugar can't push the spirit part over 100%
  const lo = liqueurDensity(a, 0, k); const hi = liqueurDensity(a, maxS, k);
  if (!(d20 >= lo - 0.05) || !(d20 <= hi)) return NaN;
  if (d20 <= lo) return 0;
  let L = 0; let H = maxS;
  for (let i = 0; i < 80; i += 1) { const m = (L + H) / 2; if (liqueurDensity(a, m, k) < d20) L = m; else H = m; }
  return (L + H) / 2;
}

/**
 * Blend a syrup of known density with a spirit (and water, if any) (Joe,
 * 27 Oct 2026). The syrup's sugar comes from its density (sugarFromDensity at
 * 0% ABV); the blend's volume is found from its mass, so the contraction of
 * alcohol with water is allowed for. Volumes in litres at 20 °C, densities in
 * kg/m³. Returns null when the figures don't make a blend.
 */
export function syrupBlend({ syrupL, syrupDensity, syrupTemp = 20, spiritL, spiritAbv, waterL = 0, k = 0.625 }) {
  const Vy = Number(syrupL) || 0; const Vs = Number(spiritL) || 0; const Vw = Number(waterL) || 0;
  const a = Number(spiritAbv); const d = Number(syrupDensity);
  if (Vy < 0 || Vs < 0 || Vw < 0 || Vy + Vs + Vw <= 0) return null;
  const syrupSugar = Vy > 0 ? sugarFromDensity(d, 0, syrupTemp, k) : 0; // g/L
  if (Vy > 0 && !Number.isFinite(syrupSugar)) return null;
  if (Vs > 0 && !(a >= 0 && a <= 100)) return null;
  const syrupD20 = Vy > 0 ? liqueurDensity(0, syrupSugar, k) : 0;
  const mass = (Vy * syrupD20 + (Vs > 0 ? Vs * rho(pFromAbv(a), 20) : 0) + Vw * RHO_WATER_20) / 1000; // kg
  const lpa = (Vs * a) / 100;
  const sugarKg = (Vy * syrupSugar) / 1000;
  const massAt = (V) => (liqueurDensity(Math.min((100 * lpa) / V, 99.99), (1000 * sugarKg) / V, k) * V) / 1000;
  const sum = Vy + Vs + Vw;
  let lo = sum * 0.8; let hi = sum * 1.05;
  if (!(Number.isFinite(massAt(lo)) && Number.isFinite(massAt(hi)))) return null;
  // Mass at a given volume rises with the volume (same alcohol and sugar, more water).
  for (let i = 0; i < 80; i += 1) { const m = (lo + hi) / 2; if (massAt(m) < mass) lo = m; else hi = m; }
  const V = (lo + hi) / 2;
  const abv = (100 * lpa) / V; const sugar = (1000 * sugarKg) / V;
  return { volume: V, abv, sugar, density: liqueurDensity(abv, sugar, k), apparent: apparentAbv(abv, sugar, k), syrupSugar, lpa, sugarKg, mass, contraction: sum - V };
}

/**
 * How much syrup, spirit and water to make `volume` litres at a target
 * strength and/or sugar (g/L) (Joe, 27 Oct 2026). With both targets, water
 * makes up the rest; with one, there's no water and the other follows.
 * Returns { error } when it can't be done.
 */
export function syrupTarget({ syrupDensity, syrupTemp = 20, spiritAbv, volume, targetAbv, targetSugar, k = 0.625 }) {
  const V = Number(volume); const a = Number(spiritAbv);
  const tA = targetAbv === '' || targetAbv == null ? null : Number(targetAbv);
  const tS = targetSugar === '' || targetSugar == null ? null : Number(targetSugar);
  if (!(V > 0)) return { error: 'Enter the volume to make.' };
  if (tA == null && tS == null) return { error: 'Enter a target strength, a target sugar, or both.' };
  const syrupSugar = sugarFromDensity(Number(syrupDensity), 0, syrupTemp, k);
  if (tS != null && !(syrupSugar > 0)) return { error: "The syrup's density doesn't give any sugar — check it (water is 998.2 kg/m³)." };
  if (tA != null && !(a > 0)) return { error: "Enter the spirit's strength." };
  if (tA != null && tA >= a) return { error: `The target strength must be below the spirit's (${a}% vol).` };
  if (tS != null && tS >= syrupSugar) return { error: `The target sugar must be below the syrup's (about ${Math.round(syrupSugar)} g/L).` };
  const blend = (Vy, Vs, Vw) => syrupBlend({ syrupL: Vy, syrupDensity, syrupTemp, spiritL: Vs, spiritAbv: a, waterL: Vw, k });
  // Find the amount x (0…2V) of the free ingredient that makes exactly V litres.
  const solve = (make) => {
    const at0 = make(0);
    if (!at0 || at0.volume > V) return null;
    let lo = 0; let hi = 2 * V;
    for (let i = 0; i < 80; i += 1) { const m = (lo + hi) / 2; const r = make(m); if (r && r.volume < V) lo = m; else hi = m; }
    return (lo + hi) / 2;
  };
  let Vy; let Vs; let Vw = 0;
  if (tA != null && tS != null) {
    Vy = (tS * V) / syrupSugar; Vs = (tA * V) / a;
    Vw = solve((x) => blend(Vy, Vs, x));
    if (Vw == null) return { error: `Can't be done: the syrup and spirit alone come to more than ${V} L. Lower one of the targets, or use a stronger spirit or a thicker syrup.` };
  } else if (tS != null) {
    Vy = (tS * V) / syrupSugar;
    Vs = solve((x) => blend(Vy, x, 0));
    if (Vs == null) return { error: `Can't be done: the syrup alone comes to more than ${V} L.` };
  } else {
    Vs = (tA * V) / a;
    Vy = solve((x) => blend(x, Vs, 0));
    if (Vy == null) return { error: `Can't be done: the spirit alone comes to more than ${V} L.` };
  }
  return { syrupL: Vy, spiritL: Vs, waterL: Vw, result: blend(Vy, Vs, Vw), syrupSugar };
}
